import React from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertTriangle, Info, Activity, FileText, ChevronDown, AlertCircle, Check, Shield } from 'lucide-react';
import { ThymosinAlpha1Diagram } from '../../assets/diagrams/ThymosinAlpha1Diagram';
import KeyTakeaway from '@/components/ui/key-takeaway';

export default function ThymosinAlpha1Content() {
  return (
    <ScrollArea className="h-[calc(100vh-16rem)] pr-4">
      <div className="mb-6 flex gap-6 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <h2 className="text-2xl font-bold mb-4 text-blue-800">Thymosin Alpha-1 Overview</h2>
          <p className="mb-4">
            Thymosin Alpha-1 (TA-1) is a 28-amino acid peptide originally isolated from thymus tissue that plays a crucial role in immune system modulation. 
            As a key thymic hormone, TA-1 primarily enhances T-cell and dendritic cell function, promoting the production of Th1 cytokines (such as IL-2, IL-12, and IFN-γ) 
            and activating cytotoxic lymphocytes.
          </p>
          <p className="mb-4">
            Beyond immune enhancement, TA-1 demonstrates direct anti-proliferative effects against tumor and viral cells. It interacts with toll-like 
            receptors (TLRs) on immune cells to trigger responses and boosts antioxidant enzymes like superoxide dismutase and glutathione peroxidase 
            to reduce oxidative stress.
          </p>
          <p>
            This dual mechanism of immune stimulation and anti-proliferation makes TA-1 a valuable therapeutic agent for a range of conditions, 
            from chronic viral infections to cancer adjuvant therapy and immunodeficiency states.
          </p>
        </div>
        <div className="flex-1 min-w-[400px] flex items-center justify-center">
          <div className="w-[90%]">
            <ThymosinAlpha1Diagram />
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4 text-blue-700">Key Mechanisms and Pathway Interactions</h3>
        
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm mb-6">
          <table className="w-full border-collapse bg-white text-sm">
            <thead>
              <tr>
                <th className="border-b border-gray-200 bg-gray-50 p-4 text-left font-medium text-gray-700">Pathway</th>
                <th className="border-b border-gray-200 bg-gray-50 p-4 text-left font-medium text-gray-700">Mechanism</th>
                <th className="border-b border-gray-200 bg-gray-50 p-4 text-left font-medium text-gray-700">Clinical Relevance</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border-b border-gray-200 p-4 font-medium text-blue-600">TLR Signaling</td>
                <td className="border-b border-gray-200 p-4 text-gray-700">
                  Activates TLR signaling in dendritic cells and macrophages, triggering innate immune responses
                </td>
                <td className="border-b border-gray-200 p-4 text-gray-700">
                  Enhances pathogen recognition and antigen presentation
                </td>
              </tr>
              <tr>
                <td className="border-b border-gray-200 p-4 font-medium text-purple-600">Cytokine Induction</td>
                <td className="border-b border-gray-200 p-4 text-gray-700">
                  Upregulates IL-2, IL-12, and IFN-γ production; enhances IL-2 receptor expression
                </td>
                <td className="border-b border-gray-200 p-4 text-gray-700">
                  Amplifies T-cell proliferation and cytotoxic responses against viruses and tumors
                </td>
              </tr>
              <tr>
                <td className="border-b border-gray-200 p-4 font-medium text-green-600">Antioxidant Defense</td>
                <td className="border-b border-gray-200 p-4 text-gray-700">
                  Boosts SOD and glutathione peroxidase activity
                </td>
                <td className="border-b border-gray-200 p-4 text-gray-700">
                  Reduces oxidative stress and cellular damage during immune activation
                </td>
              </tr>
              <tr>
                <td className="border-b border-gray-200 p-4 font-medium text-red-600">Direct Anti-proliferative</td>
                <td className="border-b border-gray-200 p-4 text-gray-700">
                  Directly inhibits tumor and viral cell growth
                </td>
                <td className="border-b border-gray-200 p-4 text-gray-700">
                  Complementary mechanism to immune-mediated tumor/virus control
                </td>
              </tr>
              <tr>
                <td className="p-4 font-medium text-amber-600">Checkpoint Modulation</td>
                <td className="p-4 text-gray-700">
                  Intersects with immune checkpoint pathways
                </td>
                <td className="p-4 text-gray-700">
                  Potential to enhance effects of checkpoint inhibitor therapies in cancer
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div className="bg-blue-50 border-l-4 border-blue-500 p-5 rounded-r-lg mb-6">
          <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
            <Info className="h-5 w-5 mr-2 text-blue-600" />
            Cross-talk with Other Immune Pathways
          </h4>
          <p className="text-blue-900">
            TA-1 acts as a crucial bridge between innate and adaptive immunity. Through TLR signaling in dendritic cells and macrophages, 
            it triggers innate pathways that subsequently lead to T-cell activation. It upregulates IL-2 receptors on lymphocytes, 
            enhancing IL-2 signaling and lymphocyte proliferation, while increased IFNs and IL-12 amplify antiviral and Th1 responses 
            that further cross-talk with NK cell and B-cell activity.
          </p>
        </div>
      </div>
      
      <KeyTakeaway>
        Thymosin Alpha-1 functions as a central immune modulator that bridges innate and adaptive immunity. Its ability to enhance T-cell function and dendritic cell activation, 
        coupled with direct anti-proliferative effects, makes it particularly valuable for treating immunodeficiency states, chronic infections, and as an adjuvant in cancer therapy.
      </KeyTakeaway>

      <div className="mt-8 mb-8">
        <h3 className="text-xl font-semibold mb-4 text-blue-700">Clinical Applications and Dosing</h3>
        
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl shadow-md p-6 mb-6">
          <h4 className="text-lg font-semibold text-indigo-800 mb-4 flex items-center">
            <Activity className="h-5 w-5 mr-2 text-indigo-600" />
            Standard Dosing Protocols
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h5 className="font-medium text-indigo-700 mb-2">Chronic Infections</h5>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">1</span>
                  <div>
                    <span className="font-semibold">Dose:</span> 0.8–1.6 mg subcutaneously
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">2</span>
                  <div>
                    <span className="font-semibold">Frequency:</span> Twice weekly
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">3</span>
                  <div>
                    <span className="font-semibold">Duration:</span> 6-12 weeks, reassess response
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">4</span>
                  <div>
                    <span className="font-semibold">Indications:</span> Chronic hepatitis B, hepatitis C (with antivirals)
                  </div>
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h5 className="font-medium text-purple-700 mb-2">Acute/Critical Illness</h5>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="bg-purple-100 text-purple-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">1</span>
                  <div>
                    <span className="font-semibold">Dose:</span> 1.6 mg subcutaneously
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-purple-100 text-purple-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">2</span>
                  <div>
                    <span className="font-semibold">Frequency:</span> Twice daily for 5 days, then daily
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-purple-100 text-purple-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">3</span>
                  <div>
                    <span className="font-semibold">Total Daily:</span> 1.6–3.2 mg/day
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-purple-100 text-purple-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">4</span>
                  <div>
                    <span className="font-semibold">Indications:</span> Sepsis, severe infections
                  </div>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="mt-4 bg-white rounded-lg p-4 shadow-sm">
            <h5 className="font-medium text-blue-700 mb-2">Cancer Adjuvant Therapy</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">1</span>
                  <div>
                    <span className="font-semibold">Dose:</span> 1.6 mg subcutaneously
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">2</span>
                  <div>
                    <span className="font-semibold">Frequency:</span> Twice weekly
                  </div>
                </li>
              </ul>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">3</span>
                  <div>
                    <span className="font-semibold">Duration:</span> Alongside and after primary therapy
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">4</span>
                  <div>
                    <span className="font-semibold">Indications:</span> NSCLC, melanoma, hepatocellular carcinoma
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <details className="bg-gray-50 rounded-lg mb-6 overflow-hidden transition-all">
          <summary className="bg-gray-100 px-6 py-3 cursor-pointer font-medium text-gray-700 flex items-center justify-between hover:bg-gray-200 transition-colors">
            <span>Storage and Handling Information</span>
            <ChevronDown className="h-5 w-5 text-gray-500" />
          </summary>
          <div className="p-6">
            <ul className="list-disc pl-5 mb-4 space-y-1 text-gray-700">
              <li>Lyophilized peptide should be stored at -20°C</li>
              <li>Reconstituted solution should be refrigerated (2-8°C) and used within 7 days</li>
              <li>Avoid repeated freeze-thaw cycles</li>
              <li>Protect from light and maintain sterility when reconstituted</li>
              <li>Reconstitute with bacteriostatic water for injection (0.9% benzyl alcohol)</li>
            </ul>
          </div>
        </details>
      </div>

      <div className="mt-6 mb-8">
        <h3 className="text-xl font-semibold mb-4 text-blue-700">Clinical Benefits and Synergistic Combinations</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 transition-all hover:shadow-lg">
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-4 py-3">
              <h4 className="text-white font-medium">Key Therapeutic Benefits</h4>
            </div>
            <div className="p-4">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Check className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">Immune Restoration:</span> Reverses immunosuppression in various clinical contexts</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Check className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">Viral Clearance:</span> Improved clearance in hepatitis B and C when combined with antivirals</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Check className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">Sepsis Outcomes:</span> Associated with 9% absolute reduction in mortality</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Check className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">Cancer Responses:</span> Improved tumor response rates and survival in several cancer types</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Check className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">Vaccine Enhancement:</span> Acts as an adjuvant to improve antibody responses</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 transition-all hover:shadow-lg">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3">
              <h4 className="text-white font-medium">Synergistic Combinations</h4>
            </div>
            <div className="p-4">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Shield className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">With Antivirals:</span> Enhanced viral clearance in hepatitis when combined with nucleoside/nucleotide analogs</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Shield className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">With Interferons:</span> Improved virologic response in hepatitis B and C compared to interferon monotherapy</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Shield className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">With Antiretrovirals:</span> Enhanced CD4⁺ T-cell recovery in HIV treatment</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Shield className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">With Chemotherapy:</span> Improved tumor response and survival in NSCLC and melanoma</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Shield className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">With Vaccines:</span> Acts as an adjuvant, enhancing both T-cell and antibody responses</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r-lg">
          <h4 className="font-semibold text-amber-800 mb-2 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2 text-amber-600" />
            Important Cautions and Contraindications
          </h4>
          <ul className="space-y-2 text-amber-900">
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span>Avoid use in patients with autoimmune diseases or on immunosuppressants for organ transplant</span>
            </li>
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span>Use with caution if combined with immune checkpoint inhibitors (effects not fully studied)</span>
            </li>
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span>Safety in pregnancy and lactation not established</span>
            </li>
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span>Ensure reputable sourcing to avoid contamination (use FDA-approved thymalfasin when possible)</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-6 mb-8">
        <h3 className="text-xl font-semibold mb-4 text-blue-700">Monitoring and Clinical Pearls</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-4 py-3">
              <h4 className="text-white font-medium">Relevant Biomarkers</h4>
            </div>
            <div className="p-4">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Biomarker</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clinical Significance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-2 text-sm font-medium text-gray-700">Absolute Lymphocyte Count</td>
                    <td className="px-4 py-2 text-sm text-gray-700">Monitor for immune restoration</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm font-medium text-gray-700">CD4⁺/CD8⁺ T-cell Counts</td>
                    <td className="px-4 py-2 text-sm text-gray-700">Indicates T-cell subpopulation recovery</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm font-medium text-gray-700">ALT/AST, Viral Load</td>
                    <td className="px-4 py-2 text-sm text-gray-700">Measure treatment success in viral hepatitis</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm font-medium text-gray-700">Tumor Markers, Imaging</td>
                    <td className="px-4 py-2 text-sm text-gray-700">Assess cancer response when used as adjuvant</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm font-medium text-gray-700">IL-2, IFN-γ (research)</td>
                    <td className="px-4 py-2 text-sm text-gray-700">Confirms immune activation in studies</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div>
            <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 mb-6">
              <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-4 py-3">
                <h4 className="text-white font-medium">Clinical Pearls</h4>
              </div>
              <div className="p-4">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="flex-shrink-0">
                      <FileText className="h-5 w-5 text-teal-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-700">Full immune reconstitution requires several weeks; not an overnight cure</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0">
                      <FileText className="h-5 w-5 text-teal-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-700">Often given in cycles (6–12 weeks on, then break) for chronic conditions</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0">
                      <FileText className="h-5 w-5 text-teal-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-700">Refrigerate vials and use aseptic technique for administration</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0">
                      <FileText className="h-5 w-5 text-teal-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-700">Mild injection-site redness is common and not an allergic reaction</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-500">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-red-800">Potential Side Effects</h4>
                  <div className="mt-2 text-sm text-red-700">
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Minor injection-site reactions (redness, irritation)</li>
                      <li>When combined with interferon: mild flu-like symptoms</li>
                      <li>No significant end-organ toxicity in clinical trials</li>
                      <li>High doses (up to 16 mg daily in short courses) well tolerated</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mb-8 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">References</h3>
        <ul className="space-y-2 text-sm text-gray-700">
          <li>Dominari A, et al. (2020). Thymosin alpha 1: A comprehensive review of pre-clinical and clinical advances. Expert Opinion on Biological Therapy.</li>
          <li>Matteucci C, et al. (2017). Thymosin Alpha 1 and HIV-1: Recent advances and future perspectives. Future Microbiology.</li>
          <li>King R, Tuthill C. (2016). Immune Modulation with Thymosin Alpha 1 Treatment. Vitamins and Hormones.</li>
          <li>Costantini C, et al. (2019). Thymosin α1 and cancer: Action on immune effector and tumor target cells. Annals of the New York Academy of Sciences.</li>
          <li>Liu F, et al. (2016). The efficacy of thymosin α1 for severe sepsis (ETASS): a multicenter, single-blind, randomized and controlled trial. Critical Care.</li>
        </ul>
      </div>
    </ScrollArea>
  );
}